import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Clock, ChevronUp, ChevronDown, X } from 'lucide-react';

/** Parse "HH:mm" or "h:mm AM/PM" → { hours: 0-23, minutes: 0-59 } */
const parseTimeValue = (timeString) => {
  if (!timeString || typeof timeString !== 'string') {
    return null;
  }

  const trimmed = timeString.trim();
  const match12 = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match12) {
    let hours = Number(match12[1]);
    const minutes = Number(match12[2]);
    const period = match12[3].toUpperCase();
    if (Number.isNaN(hours) || Number.isNaN(minutes) || minutes > 59 || hours < 1 || hours > 12) {
      return null;
    }
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return { hours, minutes };
  }

  const match24 = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (match24) {
    const hours = Number(match24[1]);
    const minutes = Number(match24[2]);
    if (Number.isNaN(hours) || Number.isNaN(minutes) || hours > 23 || minutes > 59) {
      return null;
    }
    return { hours, minutes };
  }

  return null;
};

const toValueString = (hours, minutes) =>
  `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

const periodOf = (hours) => (hours >= 12 ? 'PM' : 'AM');

const displayHour12 = (hours) => {
  if (hours === 0) return 12;
  if (hours > 12) return hours - 12;
  return hours;
};

const formatLabel = (hours, minutes, format) => {
  const mins = String(minutes).padStart(2, '0');
  if (format === '12') {
    return `${displayHour12(hours)}:${mins} ${periodOf(hours)}`;
  }
  return `${String(hours).padStart(2, '0')}:${mins}`;
};

const TimePicker = ({
  value = '',
  onChange = () => {},
  placeholder = 'Select time',
  disabled = false,
  required = false,
  label = '',
  error = '',
  className = '',
  size = 'md',
  format = '24', // '12' or '24'
  step = 15,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const timePickerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const parsed = parseTimeValue(value);
  const hours = parsed?.hours ?? 0;
  const minutes = parsed?.minutes ?? 0;
  const hasValue = Boolean(parsed);
  const period = periodOf(hours);

  const sizeClasses = {
    sm: 'h-7 min-h-7 px-2.5 text-xs leading-none',
    md: 'h-8 min-h-8 px-3 text-xs leading-none',
    lg: 'h-9 min-h-9 px-3 text-sm leading-none',
  };

  const baseClasses =
    'box-border flex w-full items-center border border-border rounded-md shadow-none focus:outline-none focus:ring-1 focus:ring-ink focus:border-ink transition-colors duration-150 bg-surface cursor-pointer';

  const inputClasses = `
    ${baseClasses}
    ${sizeClasses[size] || sizeClasses.md}
    ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
    ${disabled ? 'bg-surface-muted cursor-not-allowed opacity-60' : ''}
    ${className}
  `.trim();

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const viewportPadding = 8;
    const maxMenuWidth = Math.max(160, window.innerWidth - viewportPadding * 2);
    const menuWidth = Math.min(Math.max(rect.width, Math.min(320, maxMenuWidth)), maxMenuWidth);
    const estimatedHeight = Math.min(420, window.innerHeight - viewportPadding * 2);
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const openUp = spaceBelow < Math.min(estimatedHeight, 280) && rect.top > spaceBelow;

    let left = rect.left;
    if (left + menuWidth > window.innerWidth - viewportPadding) {
      left = Math.max(viewportPadding, window.innerWidth - menuWidth - viewportPadding);
    }
    if (left < viewportPadding) left = viewportPadding;

    setMenuStyle({
      position: 'fixed',
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
      left,
      width: menuWidth,
      maxWidth: maxMenuWidth,
      maxHeight: estimatedHeight,
      overflowY: 'auto',
      zIndex: 12050,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuStyle(null);
      return undefined;
    }
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      const inTrigger = timePickerRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inTrigger && !inMenu) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const commit = (nextHours, nextMinutes) => {
    const h = ((nextHours % 24) + 24) % 24;
    const m = Math.min(59, Math.max(0, nextMinutes));
    onChange(toValueString(h, m));
  };

  const timeOptions = useMemo(() => {
    const options = [];
    const totalMinutes = 24 * 60;
    const stepMins = Math.max(1, Number(step) || 15);
    for (let mins = 0; mins < totalMinutes; mins += stepMins) {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      options.push({
        value: toValueString(h, m),
        label: formatLabel(h, m, format),
        hours: h,
        minutes: m,
      });
    }
    return options;
  }, [format, step]);

  const quickOptions = useMemo(() => {
    // Hourly slots across the full day (AM + PM)
    return timeOptions.filter((opt) => opt.minutes === 0);
  }, [timeOptions]);

  const incrementHours = () => commit(hours + 1, minutes);
  const decrementHours = () => commit(hours - 1, minutes);

  const incrementMinutes = () => {
    const stepMins = Math.max(1, Number(step) || 15);
    const next = minutes + stepMins;
    if (next >= 60) commit(hours + 1, next % 60);
    else commit(hours, next);
  };

  const decrementMinutes = () => {
    const stepMins = Math.max(1, Number(step) || 15);
    const next = minutes - stepMins;
    if (next < 0) commit(hours - 1, 60 + next);
    else commit(hours, next);
  };

  const setPeriod = (nextPeriod) => {
    if (period === nextPeriod) return;
    // Flip by 12 hours so 7 AM ↔ 7 PM
    commit(hours >= 12 ? hours - 12 : hours + 12, minutes);
  };

  const handleTimeSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  const dropdown =
    isOpen && menuStyle
      ? createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            className="rounded-md border border-border bg-surface shadow-lg sm:shadow-sm"
          >
            <div className="p-3 sm:p-3.5">
              <div className="mb-3 flex items-center justify-center gap-3 sm:gap-4">
                <div className="text-center">
                  <div className="mb-1.5 text-xl font-semibold tabular-nums text-ink">
                    {format === '12'
                      ? displayHour12(hours)
                      : String(hours).padStart(2, '0')}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <button
                      type="button"
                      onClick={incrementHours}
                      className="touch-manipulation rounded-md p-2 hover:bg-surface-muted"
                      aria-label="Increase hours"
                    >
                      <ChevronUp className="h-4 w-4 text-ink-secondary" />
                    </button>
                    <button
                      type="button"
                      onClick={decrementHours}
                      className="touch-manipulation rounded-md p-2 hover:bg-surface-muted"
                      aria-label="Decrease hours"
                    >
                      <ChevronDown className="h-4 w-4 text-ink-secondary" />
                    </button>
                  </div>
                </div>

                <div className="text-xl font-semibold text-ink-tertiary">:</div>

                <div className="text-center">
                  <div className="mb-1.5 text-xl font-semibold tabular-nums text-ink">
                    {String(minutes).padStart(2, '0')}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <button
                      type="button"
                      onClick={incrementMinutes}
                      className="touch-manipulation rounded-md p-2 hover:bg-surface-muted"
                      aria-label="Increase minutes"
                    >
                      <ChevronUp className="h-4 w-4 text-ink-secondary" />
                    </button>
                    <button
                      type="button"
                      onClick={decrementMinutes}
                      className="touch-manipulation rounded-md p-2 hover:bg-surface-muted"
                      aria-label="Decrease minutes"
                    >
                      <ChevronDown className="h-4 w-4 text-ink-secondary" />
                    </button>
                  </div>
                </div>

                {format === '12' && (
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => setPeriod('AM')}
                      className={`h-8 min-w-[3rem] touch-manipulation rounded-md px-2.5 text-xs font-semibold ${
                        period === 'AM'
                          ? 'bg-ink text-white'
                          : 'bg-surface-muted text-ink hover:bg-canvas'
                      }`}
                    >
                      AM
                    </button>
                    <button
                      type="button"
                      onClick={() => setPeriod('PM')}
                      className={`h-8 min-w-[3rem] touch-manipulation rounded-md px-2.5 text-xs font-semibold ${
                        period === 'PM'
                          ? 'bg-ink text-white'
                          : 'bg-surface-muted text-ink hover:bg-canvas'
                      }`}
                    >
                      PM
                    </button>
                  </div>
                )}
              </div>

              <div className="border-t border-border pt-3">
                <div className="mb-2 text-xs font-medium text-ink">Quick select</div>
                <div className="grid max-h-44 grid-cols-3 gap-1.5 overflow-y-auto sm:max-h-40 sm:grid-cols-4">
                  {quickOptions.map((option) => {
                    const selected =
                      hasValue && option.hours === hours && option.minutes === minutes;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleTimeSelect(option)}
                        className={`h-9 touch-manipulation rounded-md px-1.5 text-[11px] transition-colors sm:h-8 sm:text-xs ${
                          selected
                            ? 'bg-ink text-white'
                            : 'bg-surface-muted text-ink hover:bg-canvas'
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="w-full">
      {label && (
        <label className="mb-1.5 block text-xs font-medium text-ink sm:mb-2 sm:text-sm">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className="relative" ref={timePickerRef}>
        <div
          ref={triggerRef}
          className={inputClasses}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          {...props}
        >
          <div className="flex h-full w-full items-center justify-between gap-2">
            <div className="flex min-w-0 items-center">
              <Clock className="mr-2 h-3.5 w-3.5 shrink-0 text-ink-tertiary" />
              <span className={`truncate ${hasValue ? 'text-ink' : 'text-ink-tertiary'}`}>
                {hasValue ? formatLabel(hours, minutes, format) : placeholder}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {hasValue && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-ink-tertiary hover:text-ink"
                  aria-label="Clear time"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
              <ChevronDown className="h-3.5 w-3.5 text-ink-tertiary" />
            </div>
          </div>
        </div>
      </div>

      {dropdown}

      {error && <p className="mt-1 text-xs text-red-600 sm:text-sm">{error}</p>}
    </div>
  );
};

export default TimePicker;
